import { ComponentType, useState } from "react";
import {
  ExternalLinkIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  DownloadIcon,
  EyeIcon,
  StopIcon,
} from "@heroicons/react/outline";

import { format } from "date-fns";

import scraperApi from "api";
import { dateDiff, formatTimeAgo } from "utils/dateUtils";
import { downloadFileUsingTheBlobTrick } from "utils/downloadFileUsingTheBlobTrick";
import { isValidUrl } from "utils/urlUtils";
import { isJson } from "utils/jsonUtils";
import { DAY } from "utils/timeConstants";
import Alert from "components/Alert";
import { SubTaskRow, SubtaskStatusFilter, UseJobSubtasksResult } from "hooks/hosted-scraping/useJobSubtasks";
import LabelledSelect from "components/LabelledSelect";
import { SelectOption } from "components/Select";
import { SearchInput } from "components/SearchInput";
import { TableCountText } from "components/TableCountText";
import { Job, OutputFormat } from "providers/HostedScrapingProvider/types";
import { TruncatedText } from "components/TruncatedText";
import { PureTooltip } from "components/Tooltip";
import PrevNextPagination from "components/PrevNextPagination";
import { DataTable, Column } from "components/Table";
import WarningBox from "components/WarningBox";
import StatusBadge from "components/StatusBadge";
import { EmptyUrlsState, UrlsTableErrorState } from "components/UrlsTableStates";
import ExtLink from "components/ExtLink";
import { APIErrorList, HIDDEN_TERMINATION_SLUGS } from "./APIErrorList";
import Button from "components/Button";
import Toaster from "components/Toaster";
import ViewJobResponseModal from "components/ViewJobResponseModal";
import { isJobActive, resultsExpired } from "components/hosted-scraping/job-list/utils";

// camelCase termination reason slug -> "Camel Case" for display.
const formatTerminationReason = (reason: string): string =>
  reason
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/^./, (c) => c.toUpperCase());

// Hidden and unknown slugs get generic copy instead of APIErrorList's.
const urlErrorDetail = (reason: string | null): { title: string; items: Array<{ text: React.ReactNode }> } => {
  const visibleReason = reason !== null && !HIDDEN_TERMINATION_SLUGS.has(reason) ? reason : null;
  const apiError = visibleReason !== null ? APIErrorList[visibleReason] : undefined;
  if (apiError === undefined) {
    return {
      title: "This request failed",
      items: [
        { text: "We couldn't complete this URL. Retrying the job may resolve it, or contact support if it keeps happening." },
        ...(visibleReason !== null ? [{ text: `Error code: ${formatTerminationReason(visibleReason)}` }] : []),
      ],
    };
  }
  return {
    title: apiError.title,
    items: [
      ...(apiError.description ? [{ text: apiError.description }] : []),
      ...(apiError.docsLink
        ? [{ text: <ExtLink to={apiError.docsLink.url} className="underline">{apiError.docsLink.label}</ExtLink> }]
        : []),
      ...(apiError.supportLink
        ? [{ text: <a href={apiError.supportLink.url} className="underline">{apiError.supportLink.label}</a> }]
        : []),
    ],
  };
};

const STATUS_BADGE: Record<SubTaskRow["status"], { label: string; color: string; spinner?: boolean }> = {
  finished: { label: "Done", color: "green" },
  failed: { label: "Failed", color: "red" },
  running: { label: "In progress", color: "blue", spinner: true },
  cancelled: { label: "Stopped", color: "gray" },
};

const STATUS_FILTER_OPTIONS: SelectOption<SubtaskStatusFilter>[] = [
  { name: "All", value: undefined },
  { name: "Done", value: "finished" },
  { name: "In progress", value: "running" },
  { name: "Failed", value: "failed" },
  { name: "Stopped", value: "cancelled" },
];

const RowStatusBadge = ({ status }: { status: SubTaskRow["status"] }) => {
  const badge = STATUS_BADGE[status] ?? { label: status, color: "gray" };
  return <StatusBadge label={badge.label} color={badge.color} spinner={badge.spinner} />;
};

// SDE projects (Amazon, Google, ...) store an ASIN or search term as the input.
const UrlCell = ({ url }: { url: string }) => {
  if (!isValidUrl(url)) {
    return (
      <span className="inline-flex items-center text-sm font-normal text-neutral-900 min-w-0">
        <TruncatedText text={url} />
      </span>
    );
  }
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1.5 text-sm font-normal text-neutral-900 hover:text-brandPrimary dark:hover:text-primary-600 min-w-0"
    >
      <ExternalLinkIcon className="w-4 h-4 shrink-0 text-gray-400" />
      <TruncatedText text={url} />
    </a>
  );
};

// The tooltip shows why the action is unavailable, when it is.
const IconAction = ({ Icon, label, disabledReason, onClick }: { Icon: ComponentType<{ className?: string }>; label: string; disabledReason?: string; onClick: () => void }) => (
  <PureTooltip content={disabledReason ?? label}>
    <button
      onClick={onClick}
      disabled={disabledReason !== undefined}
      aria-label={label}
      className="p-1 text-gray-600 hover:text-brandPrimary disabled:text-gray-300 disabled:cursor-not-allowed"
    >
      <Icon className="w-5 h-5" />
    </button>
  </PureTooltip>
);

const NO_RESULT = "No result available for this URL";

// Results are already in the job's output format; with none set, the body decides (JSON if it parses, else HTML).
const FORMAT_FILE: Record<string, { extension: string; mimeType: string }> = {
  csv: { extension: "csv", mimeType: "text/csv" },
  markdown: { extension: "md", mimeType: "text/markdown" },
  text: { extension: "txt", mimeType: "text/plain" },
  html: { extension: "html", mimeType: "text/html" },
  json: { extension: "json", mimeType: "application/json" },
};

const resultFile = (outputFormat: OutputFormat, body: string) => {
  if (outputFormat && FORMAT_FILE[outputFormat]) return FORMAT_FILE[outputFormat];
  return isJson(body) ? FORMAT_FILE.json : FORMAT_FILE.html;
};

const StopAction = ({ onClick, disabled }: { onClick: () => void; disabled: boolean }) => (
  <PureTooltip content={disabled ? "Stopping…" : "Stop this URL"}>
    <Button text={disabled ? "STOPPING…" : "STOP"} className="button button-tertiary inline-flex items-center gap-1.5 py-4 px-2" icon={{ Icon: StopIcon, size: "w-5 h-5" }} onClick={onClick} disabled={disabled} />
  </PureTooltip>
);

const ErrorReportToggle = ({ open, onClick }: { open: boolean; onClick: () => void }) => (
  <button onClick={onClick} aria-expanded={open} className="button button-tertiary inline-flex items-center gap-1.5 py-0 px-2">
    ERROR REPORT
    {open ? <ChevronUpIcon className="w-4 h-4" /> : <ChevronDownIcon className="w-4 h-4" />}
  </button>
);

const RetentionNotice = ({ job }: { job: Job }) => {
  const expireAt = job.resultsExpireAt ? new Date(job.resultsExpireAt) : null;
  const finishedAt = job.finishedAt ? new Date(job.finishedAt) : null;
  const retentionDays =
    expireAt && finishedAt ? Math.round(dateDiff(finishedAt, expireAt, DAY)) : null;

  return (
    <Alert
      theme="info"
      showIcon
      className="border border-info-200 rounded"
      title={
        retentionDays
          ? `Older jobs are removed after ${retentionDays} days`
          : "Older jobs are removed once their results expire"
      }
      description={
        finishedAt ? `This job was finished on ${format(finishedAt, "yyyy-MM-dd 'at' HH:mm")}` : undefined
      }
    />
  );
};

interface DPJobUrlsTableSectionProps extends UseJobSubtasksResult {
  projectId: number;
  jobId: number;
  // The job-level STOP is in progress, so every row's STOP is disabled.
  stopping: boolean;
}

export const DPJobUrlsTableSection = ({
  projectId,
  jobId,
  job,
  subtasks,
  totalCount,
  page,
  pageSize,
  setPage,
  statusFilter,
  setStatusFilter,
  searchInput,
  setSearchInput,
  hasActiveFilters,
  isLoading,
  firstLoad,
  error,
  refetch,
  retry,
  stopping,
}: DPJobUrlsTableSectionProps) => {
  const [preview, setPreview] = useState<{ id: string; url: string; response?: any } | null>(null);
  // One preview at a time, so a slower earlier response can't replace a later one.
  const [previewLoading, setPreviewLoading] = useState(false);
  const [disabledIds, setDisabledIds] = useState<Set<number>>(new Set());
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());
  const outputFormat = job?.apiParams?.outputFormat;

  const toggleExpanded = (id: number) =>
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });

  const handlePreview = async (row: SubTaskRow) => {
    if (previewLoading) return;
    setPreviewLoading(true);
    try {
      const response = await scraperApi.hostedScraping.subtaskResult(projectId, jobId, row.id);
      setPreview({ id: String(row.id), url: row.input, response });
    } catch {
      Toaster.error("Couldn't load the result for this URL. Please try again.");
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleDownload = async (row: SubTaskRow) => {
    try {
      const response = await scraperApi.hostedScraping.subtaskResult(projectId, jobId, row.id);
      const body = typeof response === "string" ? response : JSON.stringify(response, null, 2);
      const { extension, mimeType } = resultFile(outputFormat, body);
      downloadFileUsingTheBlobTrick(`job-${jobId}-url-${row.id}.${extension}`, mimeType)(body);
    } catch {
      Toaster.error("Couldn't download the result for this URL. Please try again.");
    }
  };

  const handleStop = async (row: SubTaskRow) => {
    // Double-clicks can land before the disabled re-render.
    if (stopping || disabledIds.has(row.id)) return;
    setDisabledIds((prev) => new Set(prev).add(row.id));
    try {
      await scraperApi.hostedScraping.cancelSubtask(projectId, jobId, row.id);
      Toaster.info("Stopping URL…");
      refetch();
    } catch {
      setDisabledIds((prev) => {
        const next = new Set(prev);
        next.delete(row.id);
        return next;
      });
      Toaster.error("Couldn't stop this URL. Please try again.");
    }
  };

  const hasNextPage = page * pageSize < totalCount;
  const hasPrevPage = page > 1;

  const columns: Column<SubTaskRow>[] = [
    { key: "url", header: "URL", tooltip: "The URL that was scraped", cell: (row) => <UrlCell url={row.input} /> },
    { key: "lastRun", header: "Last Run", tooltip: "When this URL last finished", cell: (row) => formatTimeAgo(row.finishedAt) },
    { key: "status", header: "Status", cell: (row) => <RowStatusBadge status={row.status} /> },
    {
      key: "actions",
      header: "",
      align: "right",
      cell: (row) => {
        if (row.status === "running") return <StopAction onClick={() => handleStop(row)} disabled={stopping || disabledIds.has(row.id)} />;
        if (row.status === "finished") {
          return (
            <div className="inline-flex items-center gap-2">
              <IconAction
                Icon={EyeIcon}
                label="Preview result"
                disabledReason={!row.hasResult ? NO_RESULT : previewLoading ? "Loading…" : undefined}
                onClick={() => handlePreview(row)}
              />
              <IconAction
                Icon={DownloadIcon}
                label="Download result"
                disabledReason={row.hasResult ? undefined : NO_RESULT}
                onClick={() => handleDownload(row)}
              />
            </div>
          );
        }
        if (row.status === "failed") return <ErrorReportToggle open={expandedIds.has(row.id)} onClick={() => toggleExpanded(row.id)} />;
        return null;
      },
    },
  ];

  const renderExpandedRow = (row: SubTaskRow) => {
    if (row.status !== "failed" || !expandedIds.has(row.id)) return null;
    const detail = urlErrorDetail(row.terminationReason);
    return (
      <div className="m-4">
        <WarningBox variant="danger" title={detail.title} items={detail.items} />
      </div>
    );
  };

  if (error) {
    return <UrlsTableErrorState error={error} onRetry={retry} />;
  }

  // Rows are kept until results expire, so only show the notice once they have. With a filter
  // active, an empty page just means no matches, and the controls must stay to clear it.
  if (job && !isJobActive(job) && resultsExpired(job) && !firstLoad && !hasActiveFilters && subtasks.length === 0) {
    return <RetentionNotice job={job} />;
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <TableCountText total={totalCount} page={page} pageSize={pageSize} isLoading={isLoading} />

        <div className="flex items-center gap-4 flex-wrap justify-end">
          <LabelledSelect
            label="Filter by"
            value={STATUS_FILTER_OPTIONS.find((option) => option.value === statusFilter) ?? STATUS_FILTER_OPTIONS[0]}
            options={STATUS_FILTER_OPTIONS}
            onChange={(option: SelectOption<SubtaskStatusFilter>) => setStatusFilter(option.value)}
            className="w-[220px]"
          />
          <SearchInput
            searchValue={searchInput}
            onSearchChange={setSearchInput}
            placeholder="Search URLs"
            className="w-64"
          />
        </div>
      </div>

      <DataTable
        columns={columns}
        rows={subtasks}
        getRowKey={(row) => String(row.id)}
        isLoading={isLoading}
        empty={<EmptyUrlsState hasActiveFilters={hasActiveFilters} description="No URL details to show for this job yet. Results may have expired or the job has not started." />}
        className="min-w-[720px]"
        renderExpandedRow={renderExpandedRow}
      />

      {totalCount > 0 && (
        <PrevNextPagination
          currentPage={page}
          hasNextPage={hasNextPage}
          hasPrevPage={hasPrevPage}
          onPageChange={setPage}
          disabled={isLoading}
        />
      )}

      <ViewJobResponseModal
        isOpen={preview !== null}
        onClose={() => setPreview(null)}
        urlData={preview}
      />
    </div>
  );
};

export default DPJobUrlsTableSection;
